/**
 * MFAFactorList Component
 *
 * Displays list of enrolled MFA factors with add/remove capabilities.
 * Supports multiple TOTP factors (up to 5).
 */

// 1. External dependencies
import React, { useState } from 'react';
import { Shield, Edit2, Check, X } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardBody,
  Button,
  Chip,
  Alert,
  Tooltip,
  Input
} from '@enfomentis/ui';
import type { Factor } from '@/types/mfa';
import { mfaService } from '@/services/MFAService';

/**
 * Props for MFAFactorList component
 */
export interface MFAFactorListProps {
  /** Array of enrolled factors */
  factors: Factor[];

  /** Callback when user clicks to add another factor */
  onAddFactor: () => void;

  /** Callback when user clicks to remove a specific factor */
  onRemoveFactor: (factorId: string) => void;

  /** Callback to refresh factors list after updates */
  onRefresh?: () => void;

  /** Loading state */
  isLoading?: boolean;
}

/**
 * Props for individual factor card
 */
interface FactorCardProps {
  /** Factor data */
  factor: Factor;

  /** Factor index (1-based for display) */
  index: number;

  /** Whether this factor can be removed (false if last factor) */
  canRemove: boolean;

  /** Callback when remove button clicked */
  onRemove: () => void;

  /** Callback when label is updated */
  onLabelUpdate: (factorId: string, newLabel: string) => Promise<void>;
}

/**
 * Individual factor card component
 */
const FactorCard: React.FC<FactorCardProps> = ({
  factor,
  index,
  canRemove,
  onRemove,
  onLabelUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(factor.label || factor.friendly_name || `Authenticator ${index}`);
  const [isSaving, setIsSaving] = useState(false);

  const createdDate = new Date(factor.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const handleSaveLabel = async () => {
    if (!editLabel.trim()) return;

    setIsSaving(true);
    try {
      await onLabelUpdate(factor.id, editLabel.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update label:', error);
      // Revert on error
      setEditLabel(factor.label || factor.friendly_name || `Authenticator ${index}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditLabel(factor.label || factor.friendly_name || `Authenticator ${index}`);
    setIsEditing(false);
  };

  const removeButton = (
    <Button
      color="danger"
      variant="light"
      size="sm"
      onPress={onRemove}
      isDisabled={!canRemove}
      aria-label={`Remove Authenticator ${index}`}
    >
      Remove
    </Button>
  );

  return (
    <div className="border border-divider rounded-lg p-4 flex items-center justify-between hover:border-primary/50 transition-colors">
      <div className="flex items-center gap-3 flex-1">
        <div className="text-primary">
          <Shield size={20} />
        </div>
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1">
            <Input
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              placeholder="Enter custom name"
              size="sm"
              className="max-w-xs"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveLabel();
                if (e.key === 'Escape') handleCancelEdit();
              }}
            />
            <Button
              isIconOnly
              size="sm"
              color="success"
              variant="light"
              onPress={handleSaveLabel}
              isLoading={isSaving}
              aria-label="Save label"
            >
              <Check size={16} />
            </Button>
            <Button
              isIconOnly
              size="sm"
              color="danger"
              variant="light"
              onPress={handleCancelEdit}
              isDisabled={isSaving}
              aria-label="Cancel"
            >
              <X size={16} />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1">
            <div className="flex-1">
              <p className="font-medium text-text-primary">
                {factor.label || factor.friendly_name || `Authenticator ${index}`}
              </p>
              <p className="text-sm text-text-secondary">
                Added: {createdDate}
              </p>
            </div>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => setIsEditing(true)}
              aria-label="Edit label"
            >
              <Edit2 size={16} />
            </Button>
          </div>
        )}
      </div>

      {!isEditing && (
        canRemove ? (
          removeButton
        ) : (
          <Tooltip
            content="You must keep at least one verified authenticator"
            placement="left"
          >
            {removeButton}
          </Tooltip>
        )
      )}
    </div>
  );
};

/**
 * MFA Factor List Component
 *
 * Features:
 * - Shows all enrolled TOTP factors
 * - Add/remove factor buttons
 * - Last-factor protection (can't remove only factor)
 * - Max factor limit (5)
 * - Status indicator
 *
 * Usage:
 * ```tsx
 * <MFAFactorList
 *   factors={factors}
 *   onAddFactor={handleAdd}
 *   onRemoveFactor={handleRemove}
 *   isLoading={loading}
 * />
 * ```
 */
export const MFAFactorList: React.FC<MFAFactorListProps> = ({
  factors,
  onAddFactor,
  onRemoveFactor,
  onRefresh,
  isLoading = false
}) => {
  // Only allow removal if more than one factor exists
  const canRemoveAny = factors.length > 1;

  // Check if max factors reached
  const isMaxReached = factors.length >= 5;

  // Filter to verified factors only for display
  const verifiedFactors = factors.filter(f => f.status === 'verified');

  // Handle label update
  const handleLabelUpdate = async (factorId: string, newLabel: string): Promise<void> => {
    const result = await mfaService.updateFactorLabel(factorId, newLabel);
    if (!result.success) {
      throw new Error(result.error.message);
    }
    // Refresh factors list to show updated label
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <Card shadow="sm">
      <CardBody className="gap-4">
        {/* Header with title and status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-text-primary">
              Multi-Factor Authentication
            </h3>
          </div>
          <Chip
            color={verifiedFactors.length > 0 ? "success" : "default"}
            variant="flat"
          >
            {verifiedFactors.length > 0 ? 'Enabled' : 'Not Enrolled'}
          </Chip>
        </div>

        {/* Factor count summary */}
        {verifiedFactors.length > 0 && (
          <p className="text-sm text-text-secondary">
            {verifiedFactors.length} {verifiedFactors.length === 1 ? 'authenticator' : 'authenticators'} configured
          </p>
        )}

        {/* List of factors */}
        {verifiedFactors.length > 0 && (
          <div className="space-y-3">
            {verifiedFactors.map((factor, index) => (
              <FactorCard
                key={factor.id}
                factor={factor}
                index={index + 1}
                canRemove={canRemoveAny}
                onRemove={() => onRemoveFactor(factor.id)}
                onLabelUpdate={handleLabelUpdate}
              />
            ))}
          </div>
        )}

        {/* Add factor button */}
        <Button
          color="primary"
          variant={verifiedFactors.length === 0 ? "solid" : "bordered"}
          onPress={onAddFactor}
          isDisabled={isLoading || isMaxReached}
          fullWidth
        >
          {verifiedFactors.length === 0 ? 'Enable MFA' : '+ Add Another Authenticator'}
        </Button>

        {/* Max factors warning */}
        {isMaxReached && (
          <Alert
            color="warning"
            variant="flat"
            title="Maximum Authenticators Reached"
            description="You have reached the maximum of 5 authenticators. Please remove an existing authenticator before adding a new one."
          />
        )}

        {/* Info about MFA */}
        {verifiedFactors.length === 0 && (
          <Alert
            color="primary"
            variant="flat"
            title="Enhance Your Security"
            description="Enable multi-factor authentication to add an extra layer of security to your account. You'll need an authenticator app like Google Authenticator or Authy."
          />
        )}
      </CardBody>
    </Card>
  );
};
